import {useEffect, useMemo} from 'react';
import {BrowserFilters} from 'plugins/modules/filter/helpers/BrowserFilters';
import {isBrowserContext} from 'core/helpers';
import {GroupFacetFilters} from 'plugins/modules/filter/helpers/GroupFacetFilters';
import {getFiltersWithProductsOrApplied} from 'plugins/modules/filter/helpers';
import {IFilterFacetResponse} from 'plugins/modules/filter/types';
import {setFilterRequestLocked, useFilterInteractionsLock} from './useFiltersRouterPush';
import {useListingContext} from 'plugins/modules/listing/context';

interface IProps {
    data?: IFilterFacetResponse & {filters?: any};
    isLoading?: boolean;
    isFetching?: boolean;
    refetch?: () => void;
}

const useFiltersLockState = (isRequestLoading: boolean) => {
    const isLocked = useFilterInteractionsLock();

    useEffect(() => {
        setFilterRequestLocked(isRequestLoading);

        return () => {
            setFilterRequestLocked(false);
        };
    }, [isRequestLoading]);

    return isLocked;
};

// Временный провайдер для каталога, пока везде не поменяем ключ filters -> items
export const useGetFiltersCatalogProviderValue = ({data, refetch, isLoading, isFetching}: IProps) => {
    const {isFetching: isListingFetching} = useListingContext<any>();

    const isRequestLoading = Boolean(!isLoading && (isFetching || isListingFetching));
    const isFiltersLocked = useFiltersLockState(isRequestLoading);

    const filterInstance = useMemo(() => {
        if (!isBrowserContext()) {
            return null;
        }

        return BrowserFilters.getInstance();
    }, []);

    return useMemo(() => {
        const {items: filters} = data || {};
        const filterList = filters ?? [];
        const filtersWithProductsOrApplied = getFiltersWithProductsOrApplied(filterList);
        const {groupedFilters} = new GroupFacetFilters(filtersWithProductsOrApplied);
        const appliedFilters = filterInstance?.getAppliedFilters(filtersWithProductsOrApplied) ?? [];

        return {
            appliedFilters: [...appliedFilters],
            filterList,
            groupedFiltersList: [...groupedFilters],
            topFilters: data?.top_filter,
            refetch,
            isLoading,
            isFiltersLocked,
        };
    }, [data, filterInstance, isLoading, refetch, isFiltersLocked]);
};

export const useGetFiltersProviderValue = ({data, refetch, isLoading, isFetching}: IProps) => {
    const {isFetching: isListingFetching} = useListingContext<any>();

    const isRequestLoading = Boolean(!isLoading && (isFetching || isListingFetching));
    const isFiltersLocked = useFiltersLockState(isRequestLoading);

    const filterInstance = useMemo(() => {
        if (!isBrowserContext()) {
            return null;
        }

        return BrowserFilters.getInstance();
    }, []);

    return useMemo(() => {
        const {filters, tabs} = (data as any) || {};
        const filterList = filters ?? [];
        const filtersWithProductsOrApplied = getFiltersWithProductsOrApplied(filterList);
        const {groupedFilters} = new GroupFacetFilters(filtersWithProductsOrApplied);
        const appliedFilters = filterInstance?.getAppliedFilters(filtersWithProductsOrApplied) ?? [];

        return {
            appliedFilters: [...appliedFilters],
            tabList: tabs,
            filterList,
            groupedFiltersList: [...groupedFilters],
            topFilters: data?.top_filter,
            refetch,
            isLoading,
            isFiltersLocked,
        };
    }, [data, filterInstance, isLoading, refetch, isFiltersLocked]);
};
